
import { useState, useEffect } from 'react';
import { CalendarEvent } from '../types';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { addDays, differenceInDays } from 'date-fns';
import { fromLocalISOString, toLocalISOString } from '@/utils/timezoneUtils';
import { supabase } from '@/integrations/supabase/client';

export const useShiftContextMenu = () => {
  const [copiedShift, setCopiedShift] = useState<CalendarEvent | null>(null);
  const [copiedWithoutGuards, setCopiedWithoutGuards] = useState<boolean>(false);
  const [copySourceDate, setCopySourceDate] = useState<Date | null>(null);
  const { t } = useLanguage();
  
  // Restore copied shift state from localStorage to allow pasting across components/weeks
  useEffect(() => {
    try {
      const storedShift = localStorage.getItem('scheduling_copied_shift');
      const storedSource = localStorage.getItem('scheduling_copied_shift_source');
      const storedWithout = localStorage.getItem('scheduling_copied_shift_without_guards');
      if (storedShift && storedSource) {
        const parsed = JSON.parse(storedShift) as CalendarEvent;
        setCopiedShift(parsed);
        setCopySourceDate(new Date(storedSource));
        setCopiedWithoutGuards(storedWithout === 'true');
      }
    } catch (e) {
      console.error('Failed to restore copied shift from storage', e);
    }
  }, []);
  
  const copyShift = (event: CalendarEvent) => {
    // Clean the event to only include fields that exist in the database
    const {
      id, 
      title, 
      description, 
      start_date, 
      end_date, 
      assigned_guards, 
      team_id, 
      location, 
      created_by,
      created_at,
      updated_at
    } = event;
    
    const sourceDate = new Date(start_date);
    setCopySourceDate(sourceDate);
    
    const newCopy: CalendarEvent = {
      id: crypto.randomUUID(), // Generate new ID for the copy
      title,
      description,
      start_date,
      end_date,
      assigned_guards: assigned_guards || [], // Keep assigned guards
      team_id,
      location,
      created_by,
      created_at: created_at || new Date().toISOString(),
      updated_at: updated_at || new Date().toISOString()
    };
    setCopiedShift(newCopy);
    setCopiedWithoutGuards(false);
    
    // Persist to localStorage so paste works across components/weeks
    try {
      localStorage.setItem('scheduling_copied_shift', JSON.stringify(newCopy));
      localStorage.setItem('scheduling_copied_shift_source', sourceDate.toISOString());
      localStorage.setItem('scheduling_copied_shift_without_guards', 'false');
    } catch (e) {
      console.error('Failed to persist copied shift', e);
    }
    toast.success(
      t('shiftCopied') || 'Shift copied', 
      { 
        description: `${event.title} - Can be pasted multiple times across any week`,
        duration: 3000
      }
    );
  };

  const copyShiftWithoutGuards = (event: CalendarEvent) => {
    // Clean the event to only include fields that exist in the database
    const {
      id, 
      title, 
      description, 
      start_date, 
      end_date, 
      team_id, 
      location, 
      created_by,
      created_at,
      updated_at
    } = event;
    
    const sourceDate = new Date(start_date);
    setCopySourceDate(sourceDate);
    
    const newCopy: CalendarEvent = {
      id: crypto.randomUUID(), // Generate new ID for the copy
      title,
      description,
      start_date,
      end_date,
      assigned_guards: [], // Clear assigned guards
      team_id,
      location,
      created_by,
      created_at: created_at || new Date().toISOString(),
      updated_at: updated_at || new Date().toISOString()
    };
    setCopiedShift(newCopy);
    setCopiedWithoutGuards(true);
    
    // Persist to localStorage so paste works across components/weeks
    try {
      localStorage.setItem('scheduling_copied_shift', JSON.stringify(newCopy));
      localStorage.setItem('scheduling_copied_shift_source', sourceDate.toISOString());
      localStorage.setItem('scheduling_copied_shift_without_guards', 'true');
    } catch (e) {
      console.error('Failed to persist copied shift', e);
    }
    toast.success(
      t('shiftCopiedWithoutGuards') || 'Shift copied without guards', 
      { 
        description: `${event.title} - Can be pasted multiple times across any week`,
        duration: 3000
      }
    );
  };
  
  const pasteShift = async (targetDate: Date): Promise<CalendarEvent | null> => {
    // Ensure we have a copied shift; if not, try to restore from localStorage
    let shiftToPaste = copiedShift;
    let sourceDateRef = copySourceDate;

    if (!shiftToPaste || !sourceDateRef) {
      try {
        const storedShift = localStorage.getItem('scheduling_copied_shift');
        const storedSource = localStorage.getItem('scheduling_copied_shift_source');
        const storedWithout = localStorage.getItem('scheduling_copied_shift_without_guards');
        if (storedShift && storedSource) {
          const parsed = JSON.parse(storedShift) as CalendarEvent;
          shiftToPaste = parsed;
          sourceDateRef = new Date(storedSource);
          setCopiedShift(parsed);
          setCopySourceDate(sourceDateRef);
          setCopiedWithoutGuards(storedWithout === 'true');
        }
      } catch (e) {
        console.error('Failed to restore copied shift for pasting', e);
      }
    }

    if (!shiftToPaste || !sourceDateRef) {
      toast.error(
        t('nothingToPaste') || 'Nothing to paste',
        { description: t('copyAShiftFirst') || 'Copy a shift first' }
      );
      return null;
    }
    
    // Use the original source date for consistent calculations
    const originalStartDate = fromLocalISOString(shiftToPaste.start_date);
    const originalEndDate = fromLocalISOString(shiftToPaste.end_date);
    
    // Get just the date part for comparison
    const sourceDate = new Date(
      sourceDateRef.getFullYear(), 
      sourceDateRef.getMonth(), 
      sourceDateRef.getDate()
    );
    
    const targetDateOnly = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate()
    );
    
    // Calculate the difference in days between original source and target
    const daysDifference = differenceInDays(targetDateOnly, sourceDate);
    
    // Create new dates by adding the difference
    const newStartDate = addDays(originalStartDate, daysDifference);
    const newEndDate = addDays(originalEndDate, daysDifference);
    
    console.log(`Pasting shift: Source date ${sourceDate.toISOString()}, Target date ${targetDateOnly.toISOString()}, Days diff: ${daysDifference}`);
    console.log(`Original start: ${originalStartDate.toISOString()}, New start: ${newStartDate.toISOString()}`);
    
    // Preserve the original time of day
    newStartDate.setHours(
      originalStartDate.getHours(),
      originalStartDate.getMinutes(),
      0, 0
    );
    
    newEndDate.setHours(
      originalEndDate.getHours(),
      originalEndDate.getMinutes(),
      0, 0
    );
    
    // Create the pasted event with only valid database fields
    const pastedEvent = {
      id: crypto.randomUUID(), // Always generate a new ID for each paste
      title: shiftToPaste.title,
      description: shiftToPaste.description,
      start_date: toLocalISOString(newStartDate),
      end_date: toLocalISOString(newEndDate),
      team_id: shiftToPaste.team_id,
      location: shiftToPaste.location,
      created_by: shiftToPaste.created_by,
      assigned_guards: copiedWithoutGuards ? [] : (shiftToPaste.assigned_guards || [])
    };
    
    try {
      // Insert the new shift into the database
      const { data, error } = await supabase
        .from('team_schedules')
        .insert(pastedEvent)
        .select('*')
        .single();
        
      if (error) throw error;
      
      console.log('Shift pasted, logging handled by database trigger');
      
      toast.success(
        copiedWithoutGuards 
          ? (t('shiftPastedWithoutGuards') || 'Shift pasted without guards')
          : (t('shiftPasted') || 'Shift pasted'),
        { 
          description: `${pastedEvent.title} - Copy remains available for more pasting`,
          duration: 2000
        }
      );
      
      // Trigger a refetch of schedules to update the UI
      document.dispatchEvent(new CustomEvent('refetch-schedules'));
      
      return data as CalendarEvent;
    } catch (error) {
      console.error('Error pasting shift:', error);
      toast.error(
        t('errorPastingShift') || 'Error pasting shift',
        { description: (error as Error).message }
      );
      return null;
    }
  };

  const clearCopiedShift = () => {
    setCopiedShift(null);
    setCopiedWithoutGuards(false);
    setCopySourceDate(null);
    try {
      localStorage.removeItem('scheduling_copied_shift');
      localStorage.removeItem('scheduling_copied_shift_source');
      localStorage.removeItem('scheduling_copied_shift_without_guards');
    } catch (e) {
      console.error('Failed to clear copied shift from storage', e);
    }
    toast.info(t('copiedShiftCleared') || 'Copied shift cleared');
  };

  const deleteShift = async (event: CalendarEvent): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('team_schedules')
        .delete()
        .eq('id', event.id);
        
      if (error) {
        throw error;
      }
      
      toast.success(
        t('shiftDeleted') || 'Shift deleted',
        { description: event.title }
      );
      
      // Trigger a refetch of schedules to update the UI
      document.dispatchEvent(new CustomEvent('refetch-schedules'));
      
      return true;
    } catch (error) {
      console.error('Error deleting shift:', error);
      toast.error(
        t('errorDeletingShift') || 'Error deleting shift',
        { description: (error as Error).message }
      );
      return false;
    }
  };
  
  return {
    copiedShift,
    copyShift,
    copyShiftWithoutGuards,
    pasteShift,
    clearCopiedShift,
    deleteShift,
    hasCopiedShift: !!copiedShift,
    copiedWithoutGuards,
    copySourceDate
  };
};
